import { CreateOrderDto } from "../../generated-client";

/**
 * Order Validation Service - Handles all order-related validation logic
 */
export class OrderValidationService {
	/**
	 * Validates if cardCode is present and not a placeholder value.
	 */
	isValidCardCode(cardCode: string | null | undefined): boolean {
		if (!cardCode || cardCode.trim() === "") {
			return false;
		}

		const normalizedCardCode = cardCode.trim().toLowerCase();
		return normalizedCardCode !== "unknown";
	}

	/**
	 * Validates order data before submission
	 * @param orderData The order data to validate
	 * @returns Error message if validation fails, null if valid
	 */
	validateOrderData(orderData: CreateOrderDto): string | null {
		// Validate customer code
		if (!this.isValidCardCode(orderData.cardCode)) {
			return "Customer code is invalid";
		}

		// Validate document lines
		if (!orderData.documentLines || orderData.documentLines.length === 0) {
			return "At least one item must be added to the order";
		}

		// Validate each document line
		for (let i = 0; i < orderData.documentLines.length; i++) {
			const line = orderData.documentLines[i];

			if (!line.itemCode || line.itemCode.trim() === "") {
				return `Item code is required for line ${i + 1}`;
			}

			if (!line.quantity || line.quantity <= 0) {
				return `Valid quantity is required for item ${line.itemCode}`;
			}
		}

		// Validate dates
		const dateValidationError = this.validateDates(orderData);
		if (dateValidationError) {
			return dateValidationError;
		}

		return null; // Validation passed
	}

	/**
	 * Validates delivery date for UI components
	 * @param selectedDate The selected date
	 * @returns Validation result object
	 */
	validateDeliveryDate(selectedDate: Date | null): {
		isValid: boolean;
		errorMessage?: string;
		valueState: "None" | "Error";
	} {
		if (!selectedDate) {
			return {
				isValid: false,
				errorMessage: "Date cannot be empty.",
				valueState: "Error",
			};
		}

		if (isNaN(selectedDate.getTime())) {
			return {
				isValid: false,
				errorMessage: "Invalid date format.",
				valueState: "Error",
			};
		}

		// Compare with today (ignore time)
		const today = new Date();
		today.setHours(0, 0, 0, 0);

		// Delivery date must be in the future
		if (selectedDate <= today) {
			return {
				isValid: false,
				errorMessage: "Date cannot be in the past.",
				valueState: "Error",
			};
		}

		return {
			isValid: true,
			valueState: "None",
		};
	}

	/**
	 * Validates order dates
	 * @param orderData Order data containing dates
	 * @returns Error message if validation fails, null if valid
	 */
	private validateDates(orderData: CreateOrderDto): string | null {
		if (!orderData.docDate) {
			return "Document date is required";
		}

		if (!orderData.docDueDate) {
			return "Due date is required";
		}

		if (orderData.docDueDate < orderData.docDate) {
			return "Due date cannot be earlier than document date";
		}

		const today = new Date();
		today.setHours(0, 0, 0, 0);

		if (orderData.docDueDate <= today) {
			return "Due date cannot be in the past or today";
		}

		return null;
	}

	/**
	 * Validates if document lines exist and are not empty
	 * @param documentLines Document lines to validate
	 * @returns True if valid
	 */
	validateDocumentLinesExist(documentLines: any[]): boolean {
		return documentLines && documentLines.length > 0;
	}
}
